import React from 'react'
import { Info, X } from 'lucide-react'
import type { KPIInfo } from './KPICard'

/**
 * Reusable info button for chart panels, tables, and other analytics sections.
 * Mirrors the popover pattern used on KPICard so operators get the same
 * definition / formula / calculation / source narrative everywhere.
 */
export interface ChartInfoButtonProps {
  title: string
  info: KPIInfo
  /** Optional override for placement; defaults to inline-flex so headers can align it next to titles. */
  className?: string
}

export function ChartInfoButton({ title, info, className }: ChartInfoButtonProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
        className={
          className ??
          'rounded-full p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300'
        }
        aria-label={`About ${title}`}
        title="What is this metric?"
      >
        <Info className="h-4 w-4" />
      </button>
      {open && <InfoPopover title={title} info={info} onClose={() => setOpen(false)} />}
    </>
  )
}

function InfoPopover({
  title,
  info,
  onClose,
}: {
  title: string
  info: KPIInfo
  onClose: () => void
}) {
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-secondary-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-secondary-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
            <p className="text-xs text-secondary-500">Metric definition & calculation</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-3 text-left text-sm">
          <section>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Definition
            </h4>
            <p className="text-secondary-800 leading-relaxed">{info.definition}</p>
          </section>
          {info.formula && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Formula
              </h4>
              <pre className="whitespace-pre-wrap rounded bg-secondary-50 px-3 py-2 font-mono text-xs text-secondary-800">
                {info.formula}
              </pre>
            </section>
          )}
          {info.calculation && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                How it's calculated
              </h4>
              <p className="text-secondary-800 leading-relaxed">{info.calculation}</p>
            </section>
          )}
          {info.source && (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Source
              </h4>
              <p className="font-mono text-xs text-secondary-600">{info.source}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
